<template lang="pug">
VBottomSheet.comment-drawer(
  ref="drawer"
  v-model="drawerModel"
  location="bottom"
  style="max-width: 700px; margin-left: auto; margin-right: auto"
  :touchless="true"
  tag="aside"
)
  VCard
    VList
      VListItem
        template(#prepend)
          VIcon {{ mdiInformationOutline }}
        VListItemTitle
          strong {{ i18n.t("TheComment.InformationTitle") }}
    VDivider

    VList
      VListSubheader
        strong {{ i18n.t("Taxonomy.LevelOfDetail") }}
      VListItem.mb-4
        template(#prepend)
          CommentaryDetailLevelDots.mb-1.mr-2(
            :length="detailLevels.length"
            :value="numDots"
            :name="detailLevelName"
          )
        | {{ detailLevelName }}
      VDivider

      template(v-if="Object.keys(contributionsByRole).length")
        CommentDrawerContributionsGroup(
          v-for="[roleName, personNames] in contributionsListItems"
          :key="roleName"
          :role-name="roleName"
          :person-names="personNames"
        )
        VDivider

      VListItem
        CommentDrawerTimes(:changed="changed")

      template(v-if="notes.length")
        VDivider
        VListSubheader
          strong {{ i18n.t("TheComment.Notes") }}
        CommentNotes(
          :notes="notes"
          @link-clicked="noteClicked"
        )

    VDivider
    VCardActions
      VBtn(
        variant="outlined"
        @click="drawerModel = false"
      )
        VIcon.mr-2 {{ mdiClose }}
        | {{ i18n.t("Global.close") }}
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { mdiClose, mdiInformationOutline } from "@mdi/js";
import { useDisplay } from "vuetify";
import type { NoteData } from "./commentBody.util.notes";
import type { Dict } from "~/types";
import { isValueDefined } from "~/units/typescriptHelpers";
import {
  detailLevels,
  numDotsPerDetailLevelWeight,
  findDetailLevel,
} from "~/static/taxonomy/detailLevels";
import { VBottomSheet } from "vuetify/components";

const i18n = useI18n();
const locale = i18n.locale;

interface Props {
  nid: number;

  changed?: Date;

  /**
   * Contributor names, keyed by contribution role names.
   */
  contributionsByRole?: Dict<string[]>;

  notes?: NoteData[];

  detailLevelWeight: number;

  /**
   * Whether the drawer is open.
   */
  open?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  contributionsByRole: () => ({}),
  notes: () => [],
});

interface Emits {
  (e: "update:open", open: boolean): void;
}

const emit = defineEmits<Emits>();

const contributionsListItems = computed<[string, string[]][]>(() =>
  Object.entries(props.contributionsByRole)
    .filter(isValueDefined)
    .filter(([, names]) => names.length),
);

const drawerModel = computed<boolean>({
  get() {
    return props.open;
  },
  set(open) {
    emit("update:open", open);
  },
});

const detailLevelName = computed(() => {
  return findDetailLevel(props.detailLevelWeight)?.name?.[locale.value] ?? "?";
});

const numDots = computed(() => {
  return numDotsPerDetailLevelWeight[props.detailLevelWeight] ?? 0;
});

/**
 * This is called when CommentNotes emits the "linkClicked" event.
 * Close the menu, so that the note in the text can be highlighted.
 * Only close the menu if the information panel is not permanent
 */
function noteClicked() {
  drawerModel.value = false;
}
</script>

<style scoped lang="scss">
.comment-drawer {
  z-index: 7;
}
</style>
